import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css/bundle";
import { Autoplay, Pagination } from "swiper/modules";
import { getHeroSlides } from "../../api/storefront";
import { getImageUrl } from "../../api/client";
import { sanitizeStorefrontUrl } from "../../utils/storefrontUrl";
import "./ContextHeroSlider.css";

/**
 * Admin-managed hero banner (3:1). Fetches slides by context + key (shop category or page slug).
 */
export default function ContextHeroSlider({ context, contextKey = "" }) {
  const [slides, setSlides] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const keyStr = useMemo(() => (contextKey != null ? String(contextKey).trim() : ""), [contextKey]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    getHeroSlides(context, keyStr).then((list) => {
      if (!cancelled) {
        setSlides(Array.isArray(list) ? list : []);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [context, keyStr]);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const validSlides = useMemo(
    () =>
      slides.filter((slide) => {
        const rawUrl = slide.imageUrl || slide.image || "";
        return !!(getImageUrl(rawUrl) || rawUrl);
      }),
    [slides]
  );

  if (!loaded || validSlides.length === 0) return null;

  const slideCount = validSlides.length;
  const enableLoop = slideCount > 1;

  return (
    <section
      className="contextHeroSlider"
      aria-roledescription="carousel"
      aria-label="Promotional banner"
    >
      <div className="contextHeroSlider__ratio">
        <Swiper
          className="contextHeroSlider__swiper"
          modules={[Autoplay, Pagination]}
          slidesPerView={1}
          slidesPerGroup={1}
          slidesPerGroupSkip={0}
          loop={false}
          rewind={enableLoop}
          watchOverflow
          onBeforeDestroy={(swiper) => {
            swiper.autoplay?.stop?.();
          }}
          autoplay={
            prefersReducedMotion || slideCount <= 1
              ? false
              : {
                  delay: 5500,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
          }
          pagination={{
            clickable: true,
            dynamicBullets: false,
          }}
          speed={prefersReducedMotion ? 0 : 500}
          grabCursor={!prefersReducedMotion}
        >
          {validSlides.map((slide, index) => {
            const rawUrl = slide.imageUrl || slide.image || "";
            const src = getImageUrl(rawUrl) || rawUrl;
            const alt = slide.alt || slide.title || "";
            const rawLink = slide.link && String(slide.link).trim() ? String(slide.link).trim() : null;
            const link = rawLink ? sanitizeStorefrontUrl(rawLink) : null;
            const isExternal = link && /^https?:\/\//i.test(link);
            const inner = (
              <>
                <img
                  className="contextHeroSlider__img"
                  src={src}
                  alt={alt}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  sizes="100vw"
                />
                {(slide.title || slide.subtitle) && (
                  <div className="contextHeroSlider__overlay">
                    {slide.title ? <p className="contextHeroSlider__title">{slide.title}</p> : null}
                    {slide.subtitle ? <p className="contextHeroSlider__subtitle">{slide.subtitle}</p> : null}
                  </div>
                )}
              </>
            );
            return (
              <SwiperSlide key={slide.id || `slide-${index}`}>
                <div className="contextHeroSlider__slide">
                  {link ? (
                    isExternal ? (
                      <a href={link} className="contextHeroSlider__link" target="_blank" rel="noopener noreferrer">
                        {inner}
                      </a>
                    ) : (
                      <Link to={link} className="contextHeroSlider__link">
                        {inner}
                      </Link>
                    )
                  ) : (
                    inner
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
}
